import React, { useState, useEffect } from 'react';
import { Table } from 'react-bootstrap';
import CryptoJS from 'crypto-js';
import api from '../api';

function AdminStats() {
  const [stats, setStats] = useState({ statsByPeriod: [] });
  const [multiplierStats, setMultiplierStats] = useState({ dailyProfits: [] });
  const [freeGameStats, setFreeGameStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const adminId = localStorage.getItem('adminId');
  const [dailyBonuses, setDailyBonuses] = useState({});
  const token = localStorage.getItem('token');
  const getApiUrl = () => {
    const hashedApiVersion = localStorage.getItem('apiVersion');
    if (hashedApiVersion) {
      const version2Hash = CryptoJS.SHA256('2').toString();
      if (hashedApiVersion === version2Hash) {
        return 'https://phoenix-bingo-2.onrender.com/api';
      }
    }
    return process.env.REACT_APP_API;
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    const amharicDays = {
      'Sunday': 'እሁድ',
      'Monday': 'ሰኞ',
      'Tuesday': 'ማክሰኞ',
      'Wednesday': 'ረቡዕ',
      'Thursday': 'ሐሙስ',
      'Friday': 'ዓርብ',
      'Saturday': 'ቅዳሜ'
    };
    
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const amharicDay = amharicDays[dayName];
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    return `${amharicDay}, ${formattedDate}`;
  };
  
  const API = getApiUrl();
  useEffect(() => {
    const fetchAllStats = async () => {
      try {
        const [regularResponse, multiplierResponse, freeGamesResponse] = await Promise.all([
          api.get(`${API}/admin/stats`),
          api.get(`${API}/admin/${adminId}/multiplier-profit-summary`),
          api.get(`${API}/admin/${adminId}/free-games-stats`)
        ]);

        const regularData = await regularResponse;
        const multiplierData = await multiplierResponse;
        const freeGamesData = await freeGamesResponse;

        setStats(regularData);
        setMultiplierStats(multiplierData);
        setFreeGameStats(freeGamesData);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllStats();
    fetchDailyBonuses();
  }, []);

  const fetchDailyBonuses = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 100);

      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      
      const response = await api.get(`${API}/bonus/daily-stats?${params}`);
      const bonusesByDate = response.reduce((acc, stat) => {
        acc[stat.date] = stat.totalBonusAmount;
        return acc;
      }, {});
      setDailyBonuses(bonusesByDate);
    } catch (error) {
      console.error('Error fetching daily bonuses:', error);
    }
  };

  // Helper function to format date consistently
  const getFormattedDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  // Combine profits for entries with the same date
  const getCombinedStats = () => {
    const combined = {};

    // Add regular game profits
    stats.statsByPeriod.forEach(period => {
      const dateStr = getFormattedDate(period.date);
      if (!combined[dateStr]) {
        combined[dateStr] = {
          regularProfit: 0,
          multiplierProfit: 0,
          totalProfit: 0,
          freeGamesCount: 0,
          freeGamesBetAmount: 0,
          date: dateStr
        };
      }
      combined[dateStr].regularProfit += Number(period.profit || 0);
    });

    // Add multiplier game profits
    multiplierStats.dailyProfits?.forEach(period => {
      const dateStr = getFormattedDate(period.date);
      if (!combined[dateStr]) {
        combined[dateStr] = {
          regularProfit: 0,
          multiplierProfit: 0,
          totalProfit: 0,
          freeGamesCount: 0,
          freeGamesBetAmount: 0,
          date: dateStr
        };
      }
      combined[dateStr].multiplierProfit += Number(period.totalProfit || 0);
    });

    freeGameStats.forEach(stat => {
      const dateStr = getFormattedDate(stat.date);
      if (!combined[dateStr]) {
        combined[dateStr] = {
          regularProfit: 0,
          multiplierProfit: 0,
          totalProfit: 0,
          freeGamesCount: 0,
          freeGamesBetAmount: 0,
          date: dateStr
        };
      }
      combined[dateStr].freeGamesCount = stat.gamesCount;
      combined[dateStr].freeGamesBetAmount = stat.totalBetAmount;
    });

    // Calculate total profits and round all numbers
    Object.values(combined).forEach(stat => {
      stat.regularProfit = Math.round(stat.regularProfit);
      stat.multiplierProfit = Math.round(stat.multiplierProfit);
      stat.totalProfit = stat.regularProfit + stat.multiplierProfit;
      stat.freeGamesBetAmount = Math.round(stat.freeGamesBetAmount);
      stat.freeGamesCount = Math.round(stat.freeGamesCount);
    });

    return combined;
  };

  const formatAmount = (amount) => {
    return amount === 0 ? '-' : `${amount} ብር`;
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const combinedStats = getCombinedStats();

  return (
    <div className='m-1 p-4 bg-teal-700 rounded-lg shadow-lg'>
      <table className="w-full bg-teal-700 rounded-lg overflow-hidden">
        <thead>
          <tr>
            <th className="bg-teal-800 text-white font-semibold py-2 px-4">ቀን</th>
            <th className="bg-teal-800 text-white font-semibold py-3 px-4">መደበኛ ትርፍ</th>
            <th className="bg-teal-800 text-white font-semibold py-3 px-4">አባዥ ትርፍ</th>
            <th className="bg-teal-800 text-white font-semibold py-3 px-4">የቤት ስፖንሰር</th>
            <th className="bg-teal-800 text-white font-semibold py-3 px-4"> ጠቅላላ ቦነስ</th> 
            <th className="bg-teal-800 text-white font-semibold py-3 px-4">ንጹህ ትርፍ</th>
          </tr>  
        </thead>
        <tbody>
          {Object.entries(combinedStats)
            .sort((a, b) => new Date(b[0]) - new Date(a[0]))
            .map(([date, profits], index) => (
              <tr 
                key={date}
                className={`
                  hover:bg-teal-500 transition-colors duration-150
                  ${index % 2 === 0 ? 'bg-teal-700 text-white' : 'bg-teal-600 text-white'}
                `}
              >
                <td className="py-2.5 px-3 ">{formatFullDate(date)}</td> 
                <td className="py-2.5 px-3 ">{formatAmount(profits.regularProfit)}</td>
                <td className="py-2.5 px-3 ">{formatAmount(profits.multiplierProfit)}</td>
                <td className="py-2.5 px-3 ">{formatAmount(profits.freeGamesBetAmount * 0.2)}</td>
                <td className="py-2.5 px-3 ">{formatAmount(dailyBonuses[date]?.toFixed(2) || 0)}</td>
                <td className="py-2.5 px-3 font-semibold"><button className='bg-teal-800 text-white rounded-lg p-2 min-w-20'>
                  {(profits.totalProfit - (profits.freeGamesBetAmount * 0.2) - (dailyBonuses[date]?.toFixed(2) || 0)).toFixed(0)} ብር
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminStats;
